import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AuthProvider } from './context/AuthContext'
import { NotificationsProvider } from './context/NotificationsContext'
import './index.css'
import App from './App.jsx'
import { UpdatePrompt } from './components/UpdatePrompt'
import { OnboardingTour } from './components/OnboardingTour'
import { ErrorBoundary } from './components/ErrorBoundary'
import { CookieBanner } from './components/CookieBanner'
import { installErrorHandlers } from './lib/errorReport'
import { captureRefFromUrl } from './lib/referral'

// Sentry — captura errores (carga ligera en sincrono).
// La integración Session Replay pesa ~100KB — la lazy-loadamos DESPUÉS del idle
// para no bloquear el first-paint. Errores tempranos igual se capturan.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      'NetworkError', 'Failed to fetch', 'Load failed', 'AbortError', 'cancelled',
      /ResizeObserver loop/,
      /extension:\/\//, /chrome-extension/, /moz-extension/,
      /googletagmanager/, /fbevents/,
      'Non-Error promise rejection captured',
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
    beforeSend(event) {
      if (event.exception?.values?.[0]?.value?.includes('cancelled')) return null
      return event
    },
  })
  // Cargar Session Replay cuando el navegador esté idle (post-hydration)
  const loadReplay = () => import('@sentry/react').then(m => {
    Sentry.addIntegration(m.replayIntegration({ maskAllText: false, blockAllMedia: false }))
  }).catch(() => {})
  if ('requestIdleCallback' in window) requestIdleCallback(loadReplay, { timeout: 5000 })
  else setTimeout(loadReplay, 3000)
}

installErrorHandlers()
captureRefFromUrl()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <NotificationsProvider>
            <App />
            <OnboardingTour />
            <UpdatePrompt />
            <CookieBanner />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
)
